import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import _ from 'lodash';
import uuid from 'uuid/v4';
import classes from '@nerdwallet/base-styles/classes';

import { ElementImpression } from '@nerdwallet/nw-client-lib/tracking';
import Accordion from '@nerdwallet/react-accordion';
import { Title, Text, Box, CenteredContainer } from '@nerdwallet/currency';
import { getFaqSchema } from '@nerdwallet/json-ld/mappings';
import getTextColor from '~/client/lib/text-color-config';

import Row from '../row/row';
import parseStyleFormatting from '../../lib/parse-style-formatting';

import './editable-panel-group.less';

import {
  stringTextAreaSpec,
  boolCheckboxSpec,
  stringSelectSpec,
  repeatable,
  textColorPicker,
} from '../../lib/editing-configs';
import parseMdlinks from '../../lib/parse-md-links';

const textAlignCenter = classes('text-align-center');

const titleSizeEnum = {
  2: 'large',
  4: 'large',
  1: 'default',
};

const EditablePanelGroup = ({
  title,
  subtitle,
  dangerouslyAllowHTMLInBody,
  panels,
  fullWidthMobile,
  contentAlignment,
  titleSize,
  size,
  textColor,
  outputSchema,
}) => {
  const colorOptions = { textColor };
  const { textColor: textColorOption } = getTextColor(colorOptions);
  const colorStyle = { color: textColorOption };
  const mapped = panels.map(({ header, body }) => ({
    answer: body,
    question: header,
  }));
  const faqSchema = getFaqSchema(mapped);
  const jsonLd = { '@context': 'http://schema.org', ...faqSchema };
  const items = panels.map(
    ({ header = 'Title goes here', body = 'Body copy goes here', key }) => ({
      children: dangerouslyAllowHTMLInBody ? (
        <Text>
          {/* eslint-disable-next-line react/no-danger -- directive added automatically by Shepherd migration */}
          <span style={colorStyle} dangerouslySetInnerHTML={{ __html: body }} />
        </Text>
      ) : (
        <Box style={colorStyle}>
          <Text>{parseMdlinks(body)}</Text>
        </Box>
      ),
      title: header,
      headingColor: textColor,
      key,
    })
  );

  const analytics = {
    enabled: true,
    payload: {
      elementName: 'landing_page_accordion_item',
    },
  };

  let mobileWidth;
  if (fullWidthMobile) {
    mobileWidth = '100%';
  } else {
    mobileWidth = '280px';
  }

  const nodeId = uuid();
  // useState is used to ensure that the trkId is unique per instance
  // and will only be set/updated when the component initially renders
  const [trkId] = useState(nodeId);

  const elementImpressionProps = {
    elementContent: title,
    id: `editable-panel-${trkId}`,
    elementDomType: 'div',
    elementName: 'Accordion',
    sectionCategory: 'EditablePanelGroup',
  };

  const alignLeft = !contentAlignment ? textAlignCenter : '';

  const TitleComponent = titleSize === '4' ? Text : Title;

  return (
    <CenteredContainer
      className="editable-panel__cont"
      style={{
        width: size === 'sm' && `${mobileWidth}`,
      }}
    >
      {title && (
        <Box
          style={colorStyle}
          className={classNames(['editable-panel__title', alignLeft])}
        >
          <TitleComponent bold size={titleSizeEnum[titleSize]}>
            {parseStyleFormatting(title)}
          </TitleComponent>
        </Box>
      )}
      {subtitle && (
        <Box
          style={colorStyle}
          className={classNames(['editable-panel__subtitle', alignLeft])}
        >
          <Text>{subtitle}</Text>
        </Box>
      )}
      <Row className="editable-panel">
        <ElementImpression {...elementImpressionProps}>
          <Accordion
            enableTracking
            items={items}
            analytics={analytics}
            id={nodeId}
          />
        </ElementImpression>
      </Row>
      {outputSchema && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}
    </CenteredContainer>
  );
};

EditablePanelGroup.defaultProps = {
  dangerouslyAllowHTMLInBody: false,
  fullWidthMobile: false,
  panels: [],
  titleSize: '2',
  size: '',
};

EditablePanelGroup.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  dangerouslyAllowHTMLInBody: PropTypes.bool,
  fullWidthMobile: PropTypes.bool,
  panels: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string,
      body: PropTypes.string,
    })
  ),
  size: PropTypes.string,
  contentAlignment: PropTypes.bool,
  titleSize: PropTypes.string,
  textColor: PropTypes.string,
  outputSchema: PropTypes.bool,
};

EditablePanelGroup.getEditingConfig = () => [
  stringTextAreaSpec('title', 'Title', {
    help: 'For GothamBlack font, surround title text with | like |this|',
  }),
  boolCheckboxSpec('contentAlignment', 'Align title and subtitle to left?'),
  stringSelectSpec('titleSize', 'Title Size', [
    { label: 'Default', value: '2' },
    { label: 'Small', value: '4' },
    { label: 'Large', value: '1' },
  ]),
  textColorPicker,
  stringTextAreaSpec('subtitle', 'Subtitle'),
  boolCheckboxSpec('fullWidthMobile', '100% width in mobile viewports?', {
    help: 'The accordion defaults to a width of 280px on mobile viewports. Check this box to make it 100% width for mobile viewports instead.',
  }),
  boolCheckboxSpec(
    'dangerouslyAllowHTMLInBody',
    'Allow HTML in panel body sections?',
    {
      help: 'WARNING: We strongly advise against making use of this feature unless you really need it.',
    }
  ),
  boolCheckboxSpec('outputSchema', 'Output as FAQpage JSON schema?', {
    help: 'Check this ONLY if 1) your content is a FAQ and 2) you want to output a FAQPage rich snippet for your FAQ. Please note that to enable enhanced search results, an editor must also un-check the "Prevent Search Engines from Following Links?" checkbox in the SEO Options section of the Wordpress editor.',
  }),
  repeatable('panels', 'Accordion Sections', 'Section', () => [
    stringTextAreaSpec('header', 'Header'),
    stringTextAreaSpec('body', 'Body'),
  ]),
];

EditablePanelGroup.displayName = 'EditablePanelGroup';

export default connect(
  createStructuredSelector({
    size: _.property('mediaQuery'),
  })
)(EditablePanelGroup);
